import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import keycloak from '@/keycloak'
import type { Personne } from '@/types'
import * as personnesService from '@/services/personnes.service'
import { oublierAvatar } from '@/composables/useAvatar'
import { messageErreur } from '@/utils/messageErreur'
import { initiales } from '@/utils/initiales'
import { oublierDonneesHorsLigne } from '@/horsLigne'

export const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref(false)
  const username = ref<string | null>(null)
  const email = ref<string | null>(null)
  /** La personne connectée côté back (id, photo), chargée avant le montage de l'app ; null si /personnes/me a échoué. */
  const moi = ref<Personne | null>(null)
  const envoiAvatar = ref(false)
  const erreurAvatar = ref<string | null>(null)
  /** Démarrée sans réseau : sans session Keycloak, l'appli se consulte sur ce que le service worker a gardé. */
  const horsLigne = ref(false)

  const user = computed(() => {
    if (!username.value) return null
    return {
      id: keycloak.subject ?? 'unknown',
      name: username.value,
      email: email.value ?? '',
      initials: initiales(username.value),
    }
  })

  function sync() {
    isAuthenticated.value = !!keycloak.authenticated
    username.value = keycloak.tokenParsed?.name ?? keycloak.tokenParsed?.preferred_username ?? null
    email.value = keycloak.tokenParsed?.email ?? null
  }

  async function chargerMoi() {
    try {
      moi.value = await personnesService.getMoi()
    } catch {
      // Sans réponse, la personne ne se reconnaît dans aucune liste et ses avatars restent en initiales
    }
  }

  /** Démarrage sans réseau : la personne est celle du dernier /personnes/me gardé. Faux sans lui (jamais connectée ici). */
  async function reprendreHorsLigne() {
    await chargerMoi()
    if (!moi.value) return false
    horsLigne.value = true
    isAuthenticated.value = true
    username.value = moi.value.name
    return true
  }

  async function changerAvatar(envoi: () => Promise<Personne>) {
    envoiAvatar.value = true
    erreurAvatar.value = null
    try {
      const personne = await envoi()
      oublierAvatar(personne.id)
      moi.value = personne
    } catch (e) {
      erreurAvatar.value = messageErreur(e)
    } finally {
      envoiAvatar.value = false
    }
  }

  function envoyerAvatar(fichier: File) {
    return changerAvatar(() => personnesService.definirAvatar(fichier))
  }

  function retirerAvatar() {
    return changerAvatar(() => personnesService.retirerAvatar())
  }

  /**
   * Rien des données gardées ne reste sur le téléphone. Hors ligne, Keycloak est injoignable : sa session survit, et
   * l'accueil rechargé n'a plus rien à consulter.
   */
  async function logout() {
    await oublierDonneesHorsLigne()
    if (horsLigne.value) window.location.replace(window.location.origin)
    else keycloak.logout({ redirectUri: window.location.origin })
  }

  /** Erreur relancée : le dialogue l'affiche et reste ouvert. */
  function apercuSuppression() {
    return personnesService.getApercuSuppression()
  }

  /**
   * Erreur relancée, rien n'a été effacé. Sinon le compte n'existe plus, ses sessions Keycloak non plus : pas de
   * déconnexion à demander (elle viserait un utilisateur supprimé), on oublie le jeton et on revient à l'accueil.
   */
  async function supprimerCompte(transferts: Record<string, string>) {
    await personnesService.supprimerCompte(transferts)
    await oublierDonneesHorsLigne()
    keycloak.clearToken()
    window.location.replace(window.location.origin)
  }

  return {
    isAuthenticated,
    user,
    username,
    email,
    moi,
    horsLigne,
    envoiAvatar,
    erreurAvatar,
    sync,
    chargerMoi,
    reprendreHorsLigne,
    envoyerAvatar,
    retirerAvatar,
    logout,
    apercuSuppression,
    supprimerCompte,
  }
})
